import { useState, useEffect } from 'react';
import { Device } from '@capacitor/device';
import { isNative } from 'utils/platform.utils';
import { getWebDeviceInfo } from 'utils/webDeviceInfo.utils';

/**
 * Hook to fetch device information for both native and web platforms
 * @returns {Object} { deviceInfo, loading, error }
 */
const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getDeviceInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let info;
        
        if (isNative()) {
          // Use Capacitor Device API for native platforms
          const nativeInfo = await Device.getInfo();
          info = {
            deviceId: nativeInfo.uuid || 'unknown',
            platform: nativeInfo.platform,
            model: nativeInfo.model,
            manufacturer: nativeInfo.manufacturer,
            operatingSystem: nativeInfo.operatingSystem,
            osVersion: nativeInfo.osVersion,
            browserName: null, // Not applicable for native
            browserVersion: null, // Not applicable for native
            screenWidth: null, // Will be filled by screen API if needed
            screenHeight: null, // Will be filled by screen API if needed
            isVirtual: nativeInfo.isVirtual,
            memUsed: nativeInfo.memUsed,
            diskFree: nativeInfo.diskFree,
            diskTotal: nativeInfo.diskTotal
          };
        } else {
          // Use web browser APIs for web platform
          info = getWebDeviceInfo();
        }
        
        setDeviceInfo(info);
      } catch (err) {
        console.error('Failed to get device info:', err);
        setError(err.message || 'Failed to get device info');
      } finally {
        setLoading(false);
      }
    };

    getDeviceInfo();
  }, []);

  return { deviceInfo, loading, error };
};

export default useDeviceInfo;
